import classNames from "classnames";
import { html, LitElement } from "lit";
import { customElement, property } from "lit/decorators.js";
import { unsafeHTML } from "lit/directives/unsafe-html.js";

import { styles as spotlightStyles } from "../../utils/spotlight";

import componentStyles from "./macro-literary-design.css";

import type {
  DetailListItem,
  MacroLiteraryDesignData,
  MacroLiteraryDesignGroup,
} from "../../types";
import type { TemplateResult } from "lit";

@customElement("bp-macro-literary-design")
export class MacroLiteraryDesign extends LitElement {
  static styles = [spotlightStyles, componentStyles];

  @property({ type: Object, attribute: "design-data" })
  data?: MacroLiteraryDesignData;

  render() {
    if (!this.data?.groups?.length) return;
    const className = classNames("container", this.data.direction);

    return html`<div class="${className}">
      ${this.data.groups.map((group, index) =>
        this.renderGroup(group, this.data?.direction ?? "", `${index + 1}`),
      )}
    </div>`;
  }

  renderGroup(
    data: MacroLiteraryDesignGroup,
    direction: string,
    id: string,
    depth = 0,
  ): TemplateResult {
    const className = classNames("group", {
      leaf: !data.subgroups?.length,
      "has-detail-list": data.detailList?.length,
      "has-title": data.title,
      "fill-background": data.fillBackground,
    });
    const reference = data.reference
      ? html`<div class="reference">
          <bp-mark color="${data.markColor}" variant="${data.markVariant}">
            ${data.reference}
          </bp-mark>
        </div>`
      : "";
    const title = data.title
      ? html`<div class="title">${unsafeHTML(data.title)}</div>`
      : "";
    const detailListClassName = classNames("detail-list", data.detailListType);
    const detailList = data.detailList?.length
      ? html`<div class="${detailListClassName}">
          <ol>
            ${data.detailList.map((item) => {
              const listItem = item as DetailListItem;
              const listRef =
                data.detailListType == "custom" && listItem.ref
                  ? html`<span class="li-ref">${listItem.ref}</span> `
                  : "";

              if (!listItem) return null;

              return html`<li
                data-indent="${listItem.indent ?? 0}"
                value="${listItem.tag ?? ""}"
              >
                ${direction == "vertical" ? listRef : ""}
                <span class="li-detail">
                  ${direction == "horizontal" ? listRef : ""}
                  ${unsafeHTML(listItem.detail ?? item)}</span
                >
              </li>`;
            })}
          </ol>
        </div>`
      : "";
    const subgroups = data.subgroups?.length
      ? html`<div class="subgroups">
          ${data.subgroups.map((subgroup, index) =>
            this.renderGroup(
              subgroup,
              direction,
              `${id}-${index + 1}`,
              depth + 1,
            ),
          )}
        </div>`
      : "";
    const tag = data.tag ? html`<div class="tag">${data.tag}</div>` : "";

    return html`<div
      class="${className}"
      data-mark-color="${data.markColor}"
      data-mark-variant="${data.markVariant}"
      data-id="group-${id}"
      data-indent="${data.indent}"
      data-depth=${depth}
    >
      ${reference} ${title} ${detailList} ${subgroups} ${tag}
    </div>`;
  }
}
